import { useState, useEffect, useCallback } from 'react';
import { api } from '@/api/client';
import type { GoogleCalendarAccount, VacancyCalendarLink, Vacancy } from '@/types';

interface Props {
  vacancies: Vacancy[];
}

export default function GoogleCalendarSection({ vacancies }: Props) {
  const [accounts, setAccounts] = useState<GoogleCalendarAccount[]>([]);
  const [links, setLinks] = useState<Record<string, VacancyCalendarLink>>({});
  const [redirectUri, setRedirectUri] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({ name: 'Google Календарь', client_id: '', client_secret: '' });

  const load = useCallback(async () => {
    try {
      const [accs, uri] = await Promise.all([
        api.get<GoogleCalendarAccount[]>('/google-calendar/accounts'),
        api.get<{ redirect_uri: string }>('/google-calendar/redirect-uri').catch(() => null),
      ]);
      setAccounts(accs || []);
      if (uri) setRedirectUri(uri.redirect_uri);

      const entries = await Promise.all(
        vacancies.map(async (v) => {
          const link = await api
            .get<VacancyCalendarLink>(`/vacancies/${v.id}/google-calendar`)
            .catch(() => null);
          return link ? ([v.id, link] as const) : null;
        }),
      );
      setLinks(Object.fromEntries(entries.filter(Boolean) as Array<readonly [string, VacancyCalendarLink]>));
    } catch { /* раздел необязательный — молчим */ }
  }, [vacancies]);

  useEffect(() => { load(); }, [load]);

  // возврат с OAuth-согласия Google
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('google');
    if (!status) return;
    setNotice(
      status === 'connected'
        ? 'Google Календарь подключён'
        : 'Не удалось подключить Google Календарь — проверьте Client ID/Secret и redirect URI',
    );
    window.history.replaceState({}, '', window.location.pathname);
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!form.client_id.trim() || !form.client_secret.trim()) {
      setNotice('Заполните Client ID и Client Secret из Google Cloud Console');
      return;
    }
    setBusy(true);
    try {
      const account = await api.post<GoogleCalendarAccount>('/google-calendar/accounts', form);
      setShowForm(false);
      setForm({ name: 'Google Календарь', client_id: '', client_secret: '' });
      await load();
      await handleConnect(account.id);
    } catch (e) {
      setNotice((e as Error).message || 'Ошибка создания аккаунта');
    }
    setBusy(false);
  };

  const handleConnect = async (accountId: string) => {
    try {
      const { auth_url } = await api.post<{ auth_url: string }>(
        `/google-calendar/accounts/${accountId}/auth`,
        {},
      );
      window.location.href = auth_url;
    } catch (e) {
      setNotice((e as Error).message || 'Не удалось начать авторизацию');
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!confirm('Отключить аккаунт Google? Вакансии перестанут писать встречи в календарь.')) return;
    setBusy(true);
    try {
      await api.del(`/google-calendar/accounts/${accountId}`);
      await load();
    } catch (e) {
      setNotice((e as Error).message || 'Ошибка удаления');
    }
    setBusy(false);
  };

  const handleRefreshCalendars = async (accountId: string) => {
    setBusy(true);
    try {
      await api.get(`/google-calendar/accounts/${accountId}/calendars?refresh=true`);
      await load();
      setNotice('Список календарей обновлён');
    } catch (e) {
      setNotice((e as Error).message || 'Не удалось получить календари');
    }
    setBusy(false);
  };

  const handleVacancyCalendar = async (vacancyId: string, value: string) => {
    // value = "" (не синхронизировать) либо "accountId::calendarId"
    setBusy(true);
    try {
      if (!value) {
        await api.put(`/vacancies/${vacancyId}/google-calendar`, { account_id: null });
      } else {
        const [accountId, calendarId] = value.split('::');
        await api.put(`/vacancies/${vacancyId}/google-calendar`, {
          account_id: accountId,
          calendar_id: calendarId,
        });
      }
      await load();
      setNotice('Календарь вакансии сохранён');
    } catch (e) {
      setNotice((e as Error).message || 'Ошибка сохранения');
    }
    setBusy(false);
  };

  const connected = accounts.filter((a) => a.status === 'connected');

  return (
    <div className="bg-white rounded-xl border border-background-200 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-base font-heading font-semibold text-foreground-900">
            Google Календарь
          </h2>
          <p className="text-xs text-foreground-500 mt-1">
            Подтверждённые кандидатом встречи попадают в выбранный календарь. У каждой вакансии
            может быть свой.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 rounded-lg bg-white border border-background-200 text-sm text-foreground-700 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5"></i>
            Подключить аккаунт
          </button>
        )}
      </div>

      {notice && <p className="text-xs text-foreground-600 mb-3">{notice}</p>}

      {showForm && (
        <div className="bg-background-50 rounded-xl border border-background-200 p-5 mb-4 space-y-3">
          <p className="text-xs text-foreground-600 leading-relaxed">
            В <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Google Cloud Console</a>{' '}
            создайте OAuth client (тип «Web application»), включите Google Calendar API и добавьте
            в «Authorized redirect URIs» адрес:
          </p>
          <code className="block text-xs bg-white border border-background-200 rounded-lg px-3 py-2 break-all">
            {redirectUri || 'https://new.hr.starec.ai/api/google-calendar/callback'}
          </code>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Название</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Client ID</span>
              <input
                type="text"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="…apps.googleusercontent.com"
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-foreground-500 mb-1.5 block">Client Secret</span>
              <input
                type="password"
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg bg-white border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreate}
              disabled={busy}
              className="px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer"
            >
              Сохранить и авторизоваться в Google
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {accounts.length > 0 && (
        <div className="space-y-2 mb-5">
          {accounts.map((acc) => (
            <div
              key={acc.id}
              className="flex items-center gap-3 flex-wrap p-4 rounded-xl border border-background-200 bg-background-50"
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  acc.status === 'connected'
                    ? 'bg-emerald-500'
                    : acc.status === 'error'
                      ? 'bg-red-500'
                      : 'bg-amber-400'
                }`}
              ></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-800">
                  {acc.name}
                  {acc.google_email && (
                    <span className="text-xs text-foreground-400 font-normal ml-2">{acc.google_email}</span>
                  )}
                </p>
                <p className="text-xs text-foreground-400">
                  {acc.status === 'connected'
                    ? `Подключён · календарей: ${acc.calendars?.length ?? 0}`
                    : acc.status === 'error'
                      ? `Ошибка: ${acc.last_error?.slice(0, 120) || 'требуется переподключение'}`
                      : 'Ожидает авторизации в Google'}
                </p>
              </div>
              {acc.status === 'connected' && (
                <button
                  onClick={() => handleRefreshCalendars(acc.id)}
                  disabled={busy}
                  className="text-xs px-3 py-1.5 rounded-lg border border-background-200 bg-white text-foreground-600 hover:border-primary-400 transition-colors cursor-pointer disabled:opacity-50"
                >
                  Обновить календари
                </button>
              )}
              <button
                onClick={() => handleConnect(acc.id)}
                className="text-xs px-3 py-1.5 rounded-lg border border-background-200 bg-white text-foreground-600 hover:border-primary-400 transition-colors cursor-pointer"
              >
                {acc.status === 'connected' ? 'Переподключить' : 'Авторизоваться'}
              </button>
              <button
                onClick={() => handleDelete(acc.id)}
                disabled={busy}
                className="text-xs px-2 py-1.5 rounded-lg text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Календарь для каждой вакансии */}
      {connected.length > 0 && vacancies.length > 0 && (
        <div>
          <p className="text-xs font-bold text-foreground-400 uppercase tracking-wide mb-2">
            Календарь по вакансиям
          </p>
          <div className="space-y-2">
            {vacancies.map((v) => {
              const link = links[v.id];
              const value =
                link?.google_calendar_account_id && link?.google_calendar_id
                  ? `${link.google_calendar_account_id}::${link.google_calendar_id}`
                  : '';
              return (
                <div
                  key={v.id}
                  className="flex items-center gap-3 flex-wrap p-3 rounded-lg border border-background-200"
                >
                  <span className="text-sm text-foreground-800 flex-1 min-w-[160px] truncate">{v.title}</span>
                  <select
                    value={value}
                    onChange={(e) => handleVacancyCalendar(v.id, e.target.value)}
                    disabled={busy}
                    className="text-xs border border-background-200 rounded-lg px-2.5 py-2 bg-background-50 text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer min-w-[240px]"
                  >
                    <option value="">Не синхронизировать</option>
                    {connected.map((acc) =>
                      (acc.calendars || []).map((cal) => (
                        <option key={`${acc.id}::${cal.id}`} value={`${acc.id}::${cal.id}`}>
                          {cal.summary}
                          {cal.primary ? ' (основной)' : ''}
                          {connected.length > 1 ? ` — ${acc.google_email || acc.name}` : ''}
                        </option>
                      )),
                    )}
                  </select>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
